import { scroll, delay } from "@theme/m";
import { matchMediaQuery } from "@theme/utilities";

class HdtStickyBanner extends HTMLElement {
  constructor() {
    super();

    this.defaultStorageKey = "hdt-sticky-banner";
    this.getTrackingStorageKey = (storageKey) =>
      `${storageKey || this.defaultStorageKey}-affiliate-tracking`;

    this.config = {};
    this.storageKey = this.defaultStorageKey;
    this.trackingStorageKey = this.getTrackingStorageKey(this.storageKey);

    this.closeButton = null;
    this.bannerLink = null;

    this.bannerDismissed = false;
    this.bannerWasDismissedThisVisit = false;

    this.trackingExecuted = false;
    this.trackingTimer = null;

    this.trackingExitController = null;
    this.trackingImage = null;

    this.scrollTriggerCleanup = null;

    this.initialScrollY = 0;
    this.maxUserScrollY = 0;
    this.scrollBaselineInitialized = false;

    this.initialScrollPercent = 0;
    this.maxScrollPercent = 0;
    this.scrollTrackingListener = null;
    this.scrollTrackingContainer = document?.scrollingElement;

    this.consentListener = null;
  }

  connectedCallback() {
    this.config = this.parseConfig();
    this.storageKey = this.getConfig("storageKey", this.defaultStorageKey);
    this.trackingStorageKey = this.getTrackingStorageKey(this.storageKey);

    this.closeButton = this.querySelector('[ref="closeButton"]');
    this.bannerLink = this.querySelector('[ref="bannerLink"]');

    this.bannerDismissed = false;
    this.bannerWasDismissedThisVisit = false;
    this.trackingExecuted = false;

    if (this.isDesignMode()) {
      this.initializeDesignMode();
      return;
    }

    this.scrollTrackingContainer = document.scrollingElement;

    this.initializeDismissal();
    this.initializeBanner();
    this.initializeAffiliateTracking();
  }

  disconnectedCallback() {
    this.cleanupAffiliateTrigger();
    this.cleanupScrollInteractionTracking();
    this.cleanupConsentListener();
  }

  /* =========================================================
   * Configuration
   * ======================================================= */

  parseConfig() {
    const rawConfig = this.dataset.config;

    if (!rawConfig) {
      console.warn("[HDT Sticky Banner] Missing data-config.");

      return {};
    }

    try {
      const parsed = JSON.parse(rawConfig);

      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("[HDT Sticky Banner] Invalid JSON configuration.", error);

      return {};
    }
  }

  getConfig(path, fallback = null) {
    const parts = Array.isArray(path) ? path : String(path).split(".");

    let value = this.config;

    for (const part of parts) {
      if (
        value === null ||
        value === undefined ||
        typeof value !== "object" ||
        !(part in value)
      ) {
        return fallback;
      }

      value = value[part];
    }

    return value ?? fallback;
  }

  initPrefixedConfig(prefix) {
    return (path, fallback = null) => {
      return this.getConfig(`${prefix}.${path}`, fallback);
    };
  }

  /* =========================================================
   * Banner
   * ======================================================= */

  showBanner() {
    this.style.display = "inline-flex";
    return;
  }

  hideBanner() {
    this.style.display = "none";
    return;
  }

  /* =========================================================
   * Shopify Design Mode
   * ======================================================= */

  isDesignMode() {
    return (
      this.getConfig("designMode", false) === true ||
      window.Shopify?.designMode === true
    );
  }

  initializeDesignMode() {
    this.showBanner();

    this.bannerLink?.addEventListener("click", (e) => {
      e.preventDefault?.();
    });

    return;
  }

  /* =========================================================
   * Generic storage
   * ======================================================= */

  createStorageRecord(durationMs) {
    const now = Date.now();

    return {
      value: "1",
      createdAt: now,
      expiresAt: now + durationMs,
    };
  }

  serializeStorageRecord(record) {
    return JSON.stringify(record);
  }

  parseStorageRecord(value) {
    if (!value) {
      return null;
    }

    try {
      const parsed = JSON.parse(value);

      if (!parsed || parsed.value !== "1") {
        return null;
      }

      const hasCreatedAt = typeof parsed.createdAt === "number";
      const hasExpiresAt = typeof parsed.expiresAt === "number";

      if (!hasCreatedAt && !hasExpiresAt) {
        return null;
      }

      if (hasCreatedAt && !Number.isFinite(parsed.createdAt)) {
        return null;
      }

      if (hasExpiresAt && !Number.isFinite(parsed.expiresAt)) {
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }

  isValidStorageRecord(value, timestampMode, durationMs) {
    const record = this.parseStorageRecord(value);

    if (!record || record.value !== "1") {
      return false;
    }

    const now = Date.now();

    if (timestampMode === "expires_at") {
      return typeof record.expiresAt === "number" && record.expiresAt > now;
    }

    if (timestampMode === "created_at") {
      return (
        typeof record.createdAt === "number" &&
        Number.isFinite(durationMs) &&
        durationMs > 0 &&
        record.createdAt + durationMs > now
      );
    }

    return false;
  }

  /* =========================================================
   * Cookie helpers
   * ======================================================= */

  encodeCookieValue(value) {
    if (value == null) {
      return "";
    }

    try {
      const bytes = new TextEncoder().encode(String(value));

      let binary = "";

      for (const byte of bytes) {
        binary += String.fromCharCode(byte);
      }

      return btoa(binary)
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "");
    } catch {
      return null;
    }
  }

  decodeCookieValue(value) {
    if (!value) {
      return null;
    }

    try {
      let base64 = String(value).replace(/-/g, "+").replace(/_/g, "/");

      while (base64.length % 4) {
        base64 += "=";
      }

      const binary = atob(base64);

      const bytes = new Uint8Array(binary.length);

      for (let index = 0; index < binary.length; index++) {
        bytes[index] = binary.charCodeAt(index);
      }

      return new TextDecoder().decode(bytes);
    } catch {
      return null;
    }
  }

  getCookieValue(key) {
    const cookies = document.cookie ? document.cookie.split("; ") : [];

    for (const cookie of cookies) {
      const separatorIndex = cookie.indexOf("=");

      if (separatorIndex === -1) {
        continue;
      }

      let cookieKey;

      try {
        cookieKey = decodeURIComponent(cookie.slice(0, separatorIndex));
      } catch {
        continue;
      }

      if (cookieKey !== key) {
        continue;
      }

      const encodedValue = cookie.slice(separatorIndex + 1);

      let decodedCookie;

      try {
        decodedCookie = decodeURIComponent(encodedValue);
      } catch {
        return null;
      }

      return this.decodeCookieValue(decodedCookie);
    }

    return null;
  }

  setCookie(key, value, maxAgeSeconds) {
    const encodedValue = this.encodeCookieValue(value);

    if (!encodedValue) {
      return false;
    }

    const maxAge = Math.max(1, Math.floor(maxAgeSeconds));

    document.cookie =
      `${encodeURIComponent(key)}=` +
      `${encodeURIComponent(encodedValue)}; ` +
      `path=/; ` +
      `max-age=${maxAge}; ` +
      `SameSite=Lax`;

    return true;
  }

  deleteCookie(key) {
    document.cookie =
      `${encodeURIComponent(key)}=; ` +
      `path=/; ` +
      `max-age=0; ` +
      `SameSite=Lax`;
  }

  getStoredValue(storageType, key) {
    try {
      switch (storageType) {
        case "session":
          return sessionStorage.getItem(key);

        case "local":
          return localStorage.getItem(key);

        case "cookie":
          return this.getCookieValue(key);

        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  saveStoredValue(storageType, key, value, expiresAt) {
    try {
      switch (storageType) {
        case "session":
          sessionStorage.setItem(key, value);
          return true;

        case "local":
          localStorage.setItem(key, value);
          return true;

        case "cookie": {
          const remainingMs = Math.max(1, expiresAt - Date.now());

          const maxAgeSeconds = Math.max(1, Math.floor(remainingMs / 1000));

          return this.setCookie(key, value, maxAgeSeconds);
        }

        default:
          return false;
      }
    } catch {
      return false;
    }
  }

  removeStoredValue(storageType, key) {
    try {
      switch (storageType) {
        case "session":
          sessionStorage.removeItem(key);
          break;

        case "local":
          localStorage.removeItem(key);
          break;

        case "cookie":
          this.deleteCookie(key);
          break;
      }
    } catch {
      // Storage unavailable.
    }
  }

  saveStorageRecord(storageType, key, durationMs) {
    const record = this.createStorageRecord(durationMs);

    const serialized = this.serializeStorageRecord(record);

    return this.saveStoredValue(storageType, key, serialized, record.expiresAt);
  }

  hasValidStorageRecord(storageType, key, durationMs, timestampMode) {
    const storedValue = this.getStoredValue(storageType, key);

    if (!storedValue) {
      return false;
    }

    if (!this.isValidStorageRecord(storedValue, timestampMode, durationMs)) {
      this.removeStoredValue(storageType, key);

      return false;
    }

    return true;
  }

  /* =========================================================
   * Duration
   * ======================================================= */

  durationToMs(duration, unit) {
    const normalizedDuration = Math.max(1, parseInt(duration, 10) || 1);

    switch (unit) {
      case "minutes":
        return normalizedDuration * 60 * 1000;

      case "hours":
        return normalizedDuration * 60 * 60 * 1000;

      case "days":
      default:
        return normalizedDuration * 24 * 60 * 60 * 1000;
    }
  }

  /* =========================================================
   * Dismissal
   * ======================================================= */

  getDismissalConfig(path, fallback = null) {
    return this.initPrefixedConfig("dismissal")(path, fallback);
  }

  isDismissed({
    persistClose,
    storageType,
    storageKey,
    durationMs,
    timestampMode,
  }) {
    if (persistClose !== true) {
      return false;
    }

    return this.hasValidStorageRecord(
      storageType,
      storageKey,
      durationMs,
      timestampMode,
    );
  }

  saveDismissal({ persistClose, storageType, storageKey, durationMs }) {
    if (persistClose !== true) {
      return false;
    }

    return this.saveStorageRecord(storageType, storageKey, durationMs);
  }

  initializeDismissal() {
    const storageType = this.getDismissalConfig("storage", "session");

    const storageKey = this.storageKey;

    const durationMs = this.durationToMs(
      this.getDismissalConfig("duration", 7),
      this.getDismissalConfig("durationUnit", "days"),
    );

    const timestampMode = this.getDismissalConfig(
      "timestampMode",
      "created_at",
    );

    const persistClose = this.getDismissalConfig("persist", false) === true;

    this.bannerDismissed = this.isDismissed({
      persistClose,
      storageType,
      storageKey,
      durationMs,
      timestampMode,
    });

    if (this.bannerDismissed) {
      this.hideBanner();

      return;
    }

    this.showBanner();

    this.closeButton?.addEventListener("click", () => {
      this.closeBanner({
        persistClose,
        storageType,
        storageKey,
        durationMs,
      });
    });
  }

  closeBanner({ persistClose, storageType, storageKey, durationMs }) {
    this.bannerWasDismissedThisVisit = true;

    if (persistClose) {
      this.saveDismissal({
        persistClose,
        storageType,
        storageKey,
        durationMs,
      });
    }

    this.bannerDismissed = true;

    if (!this.canTrackWhenClosed()) {
      this.cleanupAffiliateTrigger();
    }

    this.hideBanner();
  }

  initializeBanner() {
    if (!this.bannerLink) {
      return;
    }

    this.bannerLink.addEventListener("click", () => {
      if (!this.shouldContinueAfterManualClick()) {
        this.trackingExecuted = true;
        this.cleanupAffiliateTrigger();
        this.cleanupConsentListener();
      }
    });

    this.initializePopup();
  }

  /* =========================================================
   * Popup
   * ======================================================= */

  getPopupConfig(path, fallback = null) {
    return this.initPrefixedConfig("popup")(path, fallback);
  }
  getPopupTarget() {
    return this.getPopupConfig("target", "hdt-sticky-popup");
  }

  getLinkConfig(path, fallback = null) {
    return this.initPrefixedConfig("link")(path, fallback);
  }

  initializePopup() {
    if (
      this.bannerLink.target !== this.getPopupTarget() &&
      this.getLinkConfig("target") !== "popup"
    ) {
      return;
    }

    this.bannerLink.addEventListener("click", (event) => {
      event.preventDefault();

      const url = this.bannerLink.href || this.getLinkConfig("url");

      if (!url) {
        return;
      }

      this.openPopup(url);
    });
  }

  openPopup(url) {
    if (!url) {
      return false;
    }

    const width = Math.max(
      300,
      parseInt(this.getPopupConfig("width", 1200), 10) || 1200,
    );

    const height = Math.max(
      200,
      parseInt(this.getPopupConfig("height", 800), 10) || 800,
    );

    const position = this.getPopupConfig("position", "center");

    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;

    const popupWidth = Math.min(width, screenWidth);
    const popupHeight = Math.min(height, screenHeight);

    let left = 0;
    let top = 0;

    switch (position) {
      case "top-left":
        left = 0;
        top = 0;
        break;

      case "top-center":
        left = (screenWidth - popupWidth) / 2;
        top = 0;
        break;

      case "top-right":
        left = screenWidth - popupWidth;
        top = 0;
        break;

      case "center-left":
        left = 0;
        top = (screenHeight - popupHeight) / 2;
        break;

      case "center":
        left = (screenWidth - popupWidth) / 2;
        top = (screenHeight - popupHeight) / 2;
        break;

      case "center-right":
        left = screenWidth - popupWidth;
        top = (screenHeight - popupHeight) / 2;
        break;

      case "bottom-left":
        left = 0;
        top = screenHeight - popupHeight;
        break;

      case "bottom-center":
        left = (screenWidth - popupWidth) / 2;
        top = screenHeight - popupHeight;
        break;

      case "bottom-right":
        left = screenWidth - popupWidth;
        top = screenHeight - popupHeight;
        break;

      default:
        left = (screenWidth - popupWidth) / 2;
        top = (screenHeight - popupHeight) / 2;
    }

    const booleanFeature = (name, value) => `${name}=${value ? "yes" : "no"}`;

    const features = [
      `width=${popupWidth}`,
      `height=${popupHeight}`,
      `left=${Math.max(0, left)}`,
      `top=${Math.max(0, top)}`,

      booleanFeature(
        "resizable",
        this.getPopupConfig("resizable", true) === true,
      ),

      booleanFeature(
        "scrollbars",
        this.getPopupConfig("scrollbars", true) === true,
      ),

      booleanFeature("toolbar", this.getPopupConfig("toolbar", false) === true),

      booleanFeature(
        "location",
        this.getPopupConfig("location", false) === true,
      ),

      booleanFeature("status", this.getPopupConfig("status", false) === true),

      booleanFeature("menubar", this.getPopupConfig("menubar", false) === true),

      booleanFeature(
        "fullscreen",
        this.getPopupConfig("fullscreen", false) === true,
      ),
    ].join(",");

    const popup = window.open(url, this.getPopupTarget(), features);

    if (!popup) {
      return false;
    }

    try {
      popup.focus();
    } catch {
      // Ignore focus errors.
    }

    return true;
  }

  /* =========================================================
   * Affiliate tracking configuration
   * ======================================================= */

  getTrackingConfig(path, fallback = null) {
    return this.initPrefixedConfig("tracking")(path, fallback);
  }

  shouldTrackWhenClosed() {
    return this.getTrackingConfig("whenClosed", false) === true;
  }

  shouldTrackAfterCurrentClose() {
    return (
      this.getTrackingConfig("afterCurrentClose", true) === true &&
      this.bannerWasDismissedThisVisit === true
    );
  }

  canTrackWhenClosed() {
    return this.shouldTrackWhenClosed() || this.shouldTrackAfterCurrentClose();
  }

  shouldBlockTrackingWhenClosed() {
    return this.bannerDismissed && !this.canTrackWhenClosed();
  }

  shouldContinueAfterManualClick() {
    return this.getTrackingConfig("continueAfterManualClick", false) === true;
  }

  isTrackingEveryTime() {
    return this.getTrackingConfig("frequency", "every_time") === "every_time";
  }

  isTrackingAlreadyStored({
    isTrackingEveryTime,
    storageType,
    storageKey,
    durationMs,
    timestampMode,
  }) {
    if (isTrackingEveryTime) {
      return false;
    }

    return this.hasValidStorageRecord(
      storageType,
      storageKey,
      durationMs,
      timestampMode,
    );
  }

  saveTracking({ isTrackingEveryTime, storageType, storageKey, durationMs }) {
    if (isTrackingEveryTime) {
      return false;
    }

    return this.saveStorageRecord(storageType, storageKey, durationMs);
  }

  /* =========================================================
   * Scroll interaction state
   *
   * This state exists independently from consent.
   *
   * The goal is:
   *
   * page load
   *   ↓
   * capture initial position
   *   ↓
   * user scrolls
   *   ↓
   * consent happens
   *   ↓
   * tracking trigger is scheduled
   *
   * The scroll that happened before consent is NOT lost.
   * ======================================================= */

  initializeScrollInteractionState() {
    const container = this.scrollTrackingContainer;

    if (!container) {
      return;
    }

    this.scrollTrackingContainer = container;

    const currentScrollY = Math.max(0, container.scrollTop || 0);

    this.initialScrollY = currentScrollY;
    this.maxUserScrollY = currentScrollY;

    this.initialScrollPercent = this.getCurrentScrollPercent(container);
    this.maxScrollPercent = this.initialScrollPercent;

    this.scrollBaselineInitialized = true;

    /*
     * We need to keep observing scroll BEFORE consent so that
     * the user's interaction progress is preserved.
     */
    const onScroll = () => {
      if (this.trackingExecuted) {
        return;
      }

      const currentScrollY = Math.max(0, container.scrollTop || 0);

      if (currentScrollY > this.maxUserScrollY) {
        this.maxUserScrollY = currentScrollY;
      }

      const currentPercent = this.getCurrentScrollPercent(container);

      if (currentPercent > this.maxScrollPercent) {
        this.maxScrollPercent = currentPercent;
      }
    };

    this.scrollTrackingListener = onScroll;

    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    window.addEventListener("resize", onScroll);
  }

  cleanupScrollInteractionTracking() {
    if (this.scrollTrackingListener) {
      window.removeEventListener("scroll", this.scrollTrackingListener);
      window.removeEventListener("resize", this.scrollTrackingListener);

      this.scrollTrackingListener = null;
    }

    this.scrollTrackingContainer = null;
  }

  getCurrentScrollPercent(container = this.scrollTrackingContainer) {
    if (!container) {
      return 0;
    }

    const maxScrollY = Math.max(
      0,
      container.scrollHeight - container.clientHeight,
    );

    if (maxScrollY <= 0) {
      return 0;
    }

    const currentScrollY = Math.max(0, container.scrollTop || 0);

    return Math.min(100, (currentScrollY / maxScrollY) * 100);
  }

  getUserScrollDistance() {
    if (!this.scrollBaselineInitialized) {
      return 0;
    }

    return Math.max(0, this.maxUserScrollY - this.initialScrollY);
  }

  getUserScrollPercent() {
    const container = this.scrollTrackingContainer;

    if (!container) {
      return 0;
    }

    const maxScrollY = Math.max(
      0,
      container.scrollHeight - container.clientHeight,
    );

    if (maxScrollY <= 0) {
      return 0;
    }

    const userScrollY = this.getUserScrollDistance();

    return Math.min(100, (userScrollY / maxScrollY) * 100);
  }

  /* =========================================================
   * Affiliate initialization
   * ======================================================= */

  initializeAffiliateTracking() {
    if (this.getTrackingConfig("enabled", false) !== true) {
      //this.cleanupScrollInteractionTracking();
      return;
    }

    if (!this.getTrackingConfig("url", "")) {
      //this.cleanupScrollInteractionTracking();
      return;
    }

    if (this.shouldBlockTrackingWhenClosed()) {
      //this.cleanupScrollInteractionTracking();
      return;
    }

    const storageType = this.getTrackingConfig("storage", "local");

    const storageKey = this.trackingStorageKey;

    const durationMs = this.durationToMs(
      this.getTrackingConfig("duration", 7),
      this.getTrackingConfig("durationUnit", "days"),
    );

    const timestampMode = this.getTrackingConfig("timestampMode", "created_at");

    const isTrackingEveryTime = this.isTrackingEveryTime();

    const storedFN = () =>
      this.isTrackingAlreadyStored({
        isTrackingEveryTime,
        storageType,
        storageKey,
        durationMs,
        timestampMode,
      });

    if (storedFN()) {
      //this.cleanupScrollInteractionTracking();
      return;
    }

    const saveFN = () =>
      this.saveTracking({
        isTrackingEveryTime,
        storageType,
        storageKey,
        durationMs,
      });

    if (this.isValidTrackingTriggerForScrollInteraction()) {
      // this.initializeScrollInteractionState();
    }

    if (this.getTrackingConfig("waitForConsent", false) === true) {
      this.waitForMarketingConsent({
        saveFN,
        storedFN,
      });

      return;
    }

    this.scheduleAffiliateTracking({
      saveFN,
      storedFN,
    });
  }

  /* =========================================================
   * Consent
   * ======================================================= */

  cleanupConsentListener() {
    if (this.consentListener) {
      document.removeEventListener(
        "visitorConsentCollected",
        this.consentListener,
      );

      this.consentListener = null;
    }
  }

  waitForMarketingConsent({ saveFN, storedFN }) {
    this.cleanupConsentListener();

    this.consentListener = (event) => {
      const consent = event?.detail || null;

      if (consent?.marketingAllowed === true) {
        this.cleanupConsentListener();

        this.scheduleAffiliateTracking({
          saveFN,
          storedFN,
        });

        return;
      }

      this.checkExistingConsent({
        saveFN,
        storedFN,
      });
    };

    document.addEventListener("visitorConsentCollected", this.consentListener);

    this.initializeCustomerPrivacy({
      saveFN,
      storedFN,
    });
  }

  initializeCustomerPrivacy({ saveFN, storedFN }) {
    if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
      window.Shopify.loadFeatures(
        [
          {
            name: "consent-tracking-api",
            version: "0.1",
          },
        ],
        (error) => {
          if (error) {
            console.warn(
              "[HDT Sticky Banner] Could not load Shopify Customer Privacy API.",
              error,
            );

            return;
          }

          this.checkExistingConsent({
            saveFN,
            storedFN,
          });
        },
      );

      return;
    }

    this.checkExistingConsent({
      saveFN,
      storedFN,
    });
  }

  getConsent() {
    const customerPrivacy = window.Shopify?.customerPrivacy;

    if (
      customerPrivacy &&
      typeof customerPrivacy.marketingAllowed === "function" &&
      customerPrivacy.marketingAllowed()
    ) {
      return true;
    }

    return false;
  }

  checkExistingConsent({ saveFN, storedFN }) {
    if (getConsent()) {
      this.cleanupConsentListener();

      this.scheduleAffiliateTracking({
        saveFN,
        storedFN,
      });
    }
  }

  /* =========================================================
   * Trigger scheduling
   * ======================================================= */

  getTrackingTrigger() {
    return this.getTrackingConfig("trigger", "time");
  }

  isValidTrackingTriggerForScrollInteraction() {
    const trigger = this.getTrackingTrigger();

    if (trigger === "time") {
      return false;
    }

    return true;
  }

  scheduleAffiliateTracking({ saveFN, storedFN }) {
    if (this.trackingExecuted) {
      return;
    }

    if (this.shouldBlockTrackingWhenClosed()) {
      return;
    }

    if (storedFN()) {
      return;
    }

    this.cleanupAffiliateTrigger();

    const trigger = this.getTrackingTrigger();

    switch (trigger) {
      case "scroll":
        this.scheduleScrollTrigger(
          {
            saveFN,
            storedFN,
          },
          {
            percent: this.getTrackingConfig("scroll.percent", 50),
            userOnly: this.getTrackingConfig("scroll.userOnly", false),
            triggerIfAlreadyPast: this.getTrackingConfig(
              "scroll.triggerIfAlreadyPast",
              false,
            ),
          },
        );
        break;

      case "exit":
        this.scheduleExitTrigger(
          {
            saveFN,
            storedFN,
          },
          {
            minimumScrollEnabled: this.getTrackingConfig(
              "exit.minimumScroll.enabled",
              true,
            ),
            minimumScrollPixels: this.getTrackingConfig(
              "exit.minimumScroll.pixels",
              20,
            ),
          },
        );
        break;

      case "time":
      default:
        /*
         * Once consent is available, time tracking can simply
         * start its timer. Scroll state is irrelevant here.
         */
        //this.cleanupScrollInteractionTracking();

        this.scheduleTimeTrigger(
          {
            saveFN,
            storedFN,
          },
          {
            delay: this.getTrackingConfig("time.delay", 10),
            scheduler: this.getTrackingConfig("time.scheduler", "timeout"),
          },
        );
        break;
    }
  }

  /* =========================================================
   * Time trigger
   * ======================================================= */

  scheduleTimeTrigger({ saveFN, storedFN }, { delay: delayValue, scheduler }) {
    const seconds = Math.max(1, parseInt(delayValue, 10) || 1);

    const milliseconds = seconds * 1000;

    if (scheduler === "frame") {
      this.trackingTimer = delay(
        () =>
          this.executeAffiliateTracking({
            saveFN,
            storedFN,
          }),
        milliseconds,
      );
    } else {
      this.trackingTimer = window.setTimeout(
        () =>
          this.executeAffiliateTracking({
            saveFN,
            storedFN,
          }),
        milliseconds,
      );
    }
  }

  /* =========================================================
   * Exit intent
   * ======================================================= */

  scheduleExitTrigger(
    { saveFN, storedFN },
    { minimumScrollEnabled = true, minimumScrollPixels = 20 },
  ) {
    if (!matchMediaQuery("hover")) {
      //this.cleanupScrollInteractionTracking();

      this.scheduleExitFallback({
        saveFN,
        storedFN,
      });
      return;
    }

    const controller = new AbortController();

    this.trackingExitController = controller;

    const initialScrollY = window.scrollY || 0;
    let maxUserScrollY = initialScrollY;

    const requiredScrollPixels = Math.max(
      0,
      parseInt(minimumScrollPixels, 10) || 0,
    );

    let minimumScrollReached =
      minimumScrollEnabled !== true || requiredScrollPixels <= 0;

    const onScroll = () => {
      if (this.trackingExecuted) {
        return;
      }

      const currentScrollY = window.scrollY || 0;

      if (currentScrollY > maxUserScrollY) {
        maxUserScrollY = currentScrollY;
      }

      if (maxUserScrollY - initialScrollY >= requiredScrollPixels) {
        minimumScrollReached = true;

        window.removeEventListener("scroll", onScroll);
      }
    };

    if (!minimumScrollReached) {
      window.addEventListener("scroll", onScroll, {
        passive: true,
        signal: controller.signal,
      });
    }

    document.addEventListener(
      "mouseleave",
      (event) => {
        if (this.shouldBlockTrackingWhenClosed()) {
          this.cleanupAffiliateTrigger();
          return;
        }

        if (!minimumScrollReached) {
          return;
        }

        if (event.clientY < 10 && !this.trackingExecuted) {
          this.cleanupAffiliateTrigger();
          this.executeAffiliateTracking({ saveFN, storedFN });
        }
      },
      {
        signal: controller.signal,
      },
    );
  }

  scheduleExitFallback({ saveFN, storedFN }) {
    const fallbackTrigger = this.getTrackingConfig(
      "exit.fallback.trigger",
      "time",
    );

    if (fallbackTrigger === "scroll") {
      this.scheduleScrollTrigger(
        { saveFN, storedFN },
        {
          percent: this.getTrackingConfig("exit.fallback.scroll.percent", 50),
          userOnly: this.getTrackingConfig(
            "exit.fallback.scroll.userOnly",
            false,
          ),
          triggerIfAlreadyPast: this.getTrackingConfig(
            "exit.fallback.scroll.triggerIfAlreadyPast",
            false,
          ),
        },
      );

      return;
    }

    this.scheduleTimeTrigger(
      { saveFN, storedFN },
      {
        delay: this.getTrackingConfig("exit.fallback.time.delay", 10),
        scheduler: this.getTrackingConfig(
          "exit.fallback.time.scheduler",
          "timeout",
        ),
      },
    );
  }

  /* =========================================================
   * Scroll trigger
   * ======================================================= */

  scheduleScrollTrigger(
    { saveFN, storedFN },
    { percent = 40, userOnly = false, triggerIfAlreadyPast = false } = {},
  ) {
    const container = this.scrollTrackingContainer;

    if (!container) {
      return;
    }

    const threshold = Math.min(100, Math.max(0, Number(percent) || 0));

    const getScrollY = () => container.scrollTop;

    const getMaxScrollY = () =>
      Math.max(0, container.scrollHeight - container.clientHeight);

    const getCurrentScrollPercent = () => {
      const maxScrollY = getMaxScrollY();

      if (maxScrollY <= 0) {
        return 0;
      }

      return Math.min(100, (getScrollY() / maxScrollY) * 100);
    };

    const initialPercent = getCurrentScrollPercent();

    if (initialPercent >= threshold) {
      if (triggerIfAlreadyPast) {
        this.executeAffiliateTracking({ saveFN, storedFN });
      }

      return;
    }

    if (!userOnly) {
      let cleanup;

      cleanup = scroll(
        (progress) => {
          if (this.shouldBlockTrackingWhenClosed()) {
            return;
          }

          if (progress * 100 < threshold) return;

          this.executeAffiliateTracking({ saveFN, storedFN });

          cleanup?.();

          if (this.scrollTriggerCleanup === cleanup) {
            this.scrollTriggerCleanup = null;
          }
        },
        {
          axis: "y",
          container,
        },
      );

      this.scrollTriggerCleanup = cleanup;
      return;
    }

    this.initialScrollY = getScrollY();
    this.maxUserScrollY = this.initialScrollY;
    this.scrollBaselineInitialized = true;

    const cleanup = () => {
      window.removeEventListener("scroll", onScroll);

      window.removeEventListener("resize", onResize);
    };

    const onScroll = () => {
      if (this.shouldBlockTrackingWhenClosed()) {
        return;
      }

      const currentScrollY = getScrollY();

      if (currentScrollY > this.maxUserScrollY) {
        this.maxUserScrollY = currentScrollY;
      }

      const userScrollY = Math.max(
        0,
        this.maxUserScrollY - this.initialScrollY,
      );

      const maxScrollY = getMaxScrollY();

      if (maxScrollY <= 0) {
        return;
      }

      const userScrollPercent = Math.min(100, (userScrollY / maxScrollY) * 100);

      if (userScrollPercent < threshold) {
        return;
      }

      cleanup();
      this.scrollTriggerCleanup = null;

      this.executeAffiliateTracking({ saveFN, storedFN });
    };

    const onResize = () => {
      onScroll();
    };

    window.addEventListener("scroll", onScroll, {
      passive: true,
    });

    window.addEventListener("resize", onResize);

    this.scrollTriggerCleanup = cleanup;
  }

  /* =========================================================
   * Trigger cleanup
   * ======================================================= */

  cleanupExitTrigger() {
    if (this.trackingExitController) {
      try {
        this.trackingExitController.abort();
      } catch {
        // Ignore abort errors.
      }

      this.trackingExitController = null;
    }
  }

  cleanupAffiliateTrigger() {
    if (this.trackingTimer) {
      if (typeof this.trackingTimer === "function") {
        this.trackingTimer();
      } else {
        window.clearTimeout(this.trackingTimer);
      }

      this.trackingTimer = null;
    }

    if (this.scrollTriggerCleanup) {
      try {
        this.scrollTriggerCleanup();
      } catch {
        // Ignore cleanup errors.
      }

      this.scrollTriggerCleanup = null;
    }

    this.cleanupExitTrigger();
  }

  /* =========================================================
   * Execute tracking
   * ======================================================= */

  executeAffiliateTracking({ saveFN, storedFN }) {
    if (this.trackingExecuted) {
      return;
    }

    if (this.shouldBlockTrackingWhenClosed()) {
      this.cleanupAffiliateTrigger();
      return;
    }

    if (storedFN()) {
      this.cleanupAffiliateTrigger();
      return;
    }

    if (this.getTrackingConfig("waitForConsent", false) === true) {
      if (!getConsent()) {
        return;
      }
    }

    const affiliateUrl = this.getTrackingConfig("url", "");

    if (!affiliateUrl) {
      return;
    }

    this.trackingExecuted = true;
    this.cleanupAffiliateTrigger();
    this.cleanupConsentListener();

    saveFN();

    const mode = this.getTrackingConfig("mode", "image");

    if (mode === "link") {
      this.executeLinkTracking(affiliateUrl);

      return;
    }
    this.executeImageTracking(affiliateUrl);
  }

  /* =========================================================
   * Image tracking
   * ======================================================= */

  executeImageTracking(url) {
    const trackingImage = new Image();

    trackingImage.width = 1;
    trackingImage.height = 1;
    trackingImage.alt = "";
    trackingImage.setAttribute("aria-hidden", "true");

    this.trackingImage = trackingImage;

    trackingImage.onload = () => {
      window.setTimeout(() => {
        if (this.trackingImage === trackingImage) {
          this.trackingImage = null;
        }
      }, 1000);
    };

    trackingImage.onerror = () => {
      console.warn(
        "[HDT Sticky Banner] Affiliate tracking resource returned an error.",
      );

      if (this.trackingImage === trackingImage) {
        this.trackingImage = null;
      }
    };

    trackingImage.src = url;
  }

  /* =========================================================
   * Link tracking
   * ======================================================= */

  executeLinkTracking(expectedUrl) {
    const expectedTarget = this.getTrackingConfig("target", "_blank");

    const existingLink = this.bannerLink;

    if (
      existingLink &&
      this.isValidTrackingLink(existingLink, expectedUrl, expectedTarget)
    ) {
      this.simulateLinkClick(existingLink);
      return;
    }

    const temporaryLink = this.createTrackingLink(expectedUrl, expectedTarget);

    document.body.appendChild(temporaryLink);

    this.simulateLinkClick(temporaryLink);

    window.setTimeout(() => {
      temporaryLink.remove();
    }, 0);
  }

  isValidTrackingLink(link, expectedUrl, expectedTarget) {
    if (!link || !expectedUrl) {
      return false;
    }

    const href = link.getAttribute("href");

    if (!href) {
      return false;
    }

    const actualUrl = this.normalizeUrl(href);

    const expectedNormalizedUrl = this.normalizeUrl(expectedUrl);

    if (actualUrl !== expectedNormalizedUrl) {
      return false;
    }

    const logicalTarget = this.getLogicalTargetFromElement(link);

    return logicalTarget === expectedTarget;
  }

  getLogicalTargetFromElement(link) {
    const actualTarget = link.getAttribute("target") || "_self";

    if (actualTarget === this.getPopupTarget()) {
      return "popup";
    }

    const target = this.getLinkConfig("target", "_self");

    if (target === "popup") {
      return "popup";
    }

    return actualTarget;
  }

  normalizeUrl(url) {
    try {
      return new URL(url, window.location.href).href;
    } catch {
      return url;
    }
  }

  createTrackingLink(url, target) {
    const link = document.createElement("a");

    link.href = url;

    switch (target) {
      case "_blank":
        link.target = "_blank";
        link.rel = "noopener";
        break;

      case "_self":
        link.target = "_self";
        break;

      case "popup":
        link.target = this.getPopupTarget();

        link.addEventListener(
          "click",
          (event) => {
            event.preventDefault();

            this.openPopup(url);
          },
          {
            once: true,
          },
        );
        break;

      default:
        link.target = "_self";
        break;
    }

    link.setAttribute("aria-hidden", "true");

    link.style.position = "absolute";
    link.style.width = "1px";
    link.style.height = "1px";
    link.style.overflow = "hidden";
    link.style.clip = "rect(0 0 0 0)";
    link.style.clipPath = "inset(50%)";
    link.style.whiteSpace = "nowrap";

    return link;
  }

  simulateLinkClick(link) {
    if (!link) {
      return;
    }

    link.dispatchEvent(
      new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      }),
    );
  }
}

/* ===========================================================
 * Custom element registration
 * ========================================================= */

if (!customElements.get("hdt-sticky-banner")) {
  customElements.define("hdt-sticky-banner", HdtStickyBanner);
}
